/*
 * FAAN cart bridge / shim — Cloude/Dario 2026-09 · porting cart drawer su Horizon 4.1.5
 *
 * Modulo ESM NATIVO di Horizon. Fa due cose:
 *  1) Espone i primitivi Horizon a window.FaanCart (refreshDrawer, onCartUpdate).
 *  2) SHIM di compatibilità: fornisce al motore classico di Andrea
 *     (assets/faan-cart-extras.js, IMMUTATO) l'ambiente "Krown" che si aspetta —
 *     window.KROWN.settings.routes, window.refreshCart, e l'alias di stato drawer
 *     aperto (#site-cart-sidebar.sidebar--opened). Così il motore gira su Horizon
 *     senza modifiche e resta allineabile agli aggiornamenti di Andrea.
 *
 * Ordine di caricamento (in cart-drawer.liquid): questo modulo PRIMA del motore
 * (che è <script defer>), così window.KROWN/refreshCart esistono all'init del motore.
 */
import { sectionRenderer } from '@theme/section-renderer';
import { StandardEvents } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

const CART_SECTION = 'cart-drawer-section';
const themeRoutes = (typeof Theme !== 'undefined' && Theme.routes) || {};
const strip = (u, fb) => (u || fb).replace(/\.js$/, '');
const ADD_BASE = strip(themeRoutes.cart_add_url, '/cart/add'); // il motore appende '.js'
const CHANGE_JS = strip(themeRoutes.cart_change_url, '/cart/change') + '.js'; // usato as-is
const CART_BASE = strip(themeRoutes.cart_url, '/cart'); // il motore appende '/clear.js'

/* ---- window.FaanCart : primitivi Horizon ---- */
const FaanCart = {
  section: CART_SECTION,
  ready: true,
  refreshDrawer() {
    // GUARDIA: se il drawer era aperto e il re-render della sezione ne perde lo
    // stato [open] (il <dialog> interno non ha data-skip-node-update), lo riapriamo.
    var d0 = document.getElementById('cart-drawer');
    var wasOpen = !!(d0 && (d0.hasAttribute('open') || d0.open === true));
    return sectionRenderer.renderSection(CART_SECTION, { cache: false, mode: 'hydration' }).then(function (res) {
      if (wasOpen) {
        var d = document.getElementById('cart-drawer');
        if (d && !(d.hasAttribute('open') || d.open === true) && typeof d.open === 'function') {
          try { d.open(); } catch (e) {}
        }
      }
      return res;
    });
  },
  onCartUpdate(cb) {
    document.addEventListener(StandardEvents.cartLinesUpdate, cb);
  },
  offCartUpdate(cb) {
    document.removeEventListener(StandardEvents.cartLinesUpdate, cb);
  },
  addLine(id, properties) {
    const body = JSON.stringify({ items: [{ id: Number(id), quantity: 1, properties: properties || {} }] });
    return fetch(themeRoutes.cart_add_url || '/cart/add.js', fetchConfig('json', { body })).then(async (r) => ({
      ok: r.ok,
      status: r.status,
      json: await r.json().catch(() => ({})),
    }));
  },
  // Cambia la quantita' di una riga (usato dal dropdown qty). key = item.key.
  changeLine(key, quantity) {
    const body = JSON.stringify({ id: key, quantity: Number(quantity) });
    return fetch(CHANGE_JS, fetchConfig('json', { body })).then(async (r) => ({
      ok: r.ok,
      status: r.status,
      json: await r.json().catch(() => ({})),
    }));
  },
};
window.FaanCart = FaanCart;

/* ---- SHIM Krown per il motore classico ---- */
window.KROWN = window.KROWN || {};
window.KROWN.settings = window.KROWN.settings || {};
window.KROWN.settings.routes = {
  cart_add_url: ADD_BASE,
  cart_change_url: CHANGE_JS,
  cart_url: CART_BASE,
};
window.KROWN.settings.faan_confetti_url = (window.__FAAN && window.__FAAN.confettiUrl) || '';
// Il motore carica il confetti via window.KrownAssetLoader.loadScript: shim.
window.KrownAssetLoader = window.KrownAssetLoader || {
  loadScript: function (url) {
    if (!url || document.querySelector('script[data-faan-loaded="' + url + '"]')) return;
    var s = document.createElement('script');
    s.src = url;
    s.setAttribute('data-faan-loaded', url);
    document.head.appendChild(s);
  },
};

window.KROWN.functions = window.KROWN.functions || {};
// Il motore usa eventDispatcher solo per notificare altri componenti Krown (assenti su Horizon): no-op.
if (typeof window.KROWN.functions.eventDispatcher !== 'function') {
  window.KROWN.functions.eventDispatcher = function () {};
}

/* Il morph di Horizon NON cambia i figli di #AjaxCartForm, quindi l'observer
   childList del motore (che su Krown scattava allo swap di innerHTML) non si
   attiva. "Poke": aggiungiamo/togliamo un figlio marker per farlo scattare dopo
   che il morph ha aggiornato lo snapshot. */
function pokeEngine() {
  var form = document.getElementById('AjaxCartForm');
  if (!form) return;
  var m = document.createElement('span');
  m.setAttribute('data-faan-poke', '');
  m.hidden = true;
  form.appendChild(m);
  requestAnimationFrame(function () {
    if (m.parentNode) m.parentNode.removeChild(m);
  });
}

// Punto di re-render canonico che il motore chiama dopo ogni sua mutazione.
window.refreshCart = function () {
  try {
    if (window.FaanCart && window.FaanCart.refreshDrawer) {
      return window.FaanCart.refreshDrawer()
        .then(function () { pokeEngine(); })
        .catch(function () {});
    }
  } catch (e) {
    /* non pronto: la modifica comparirà al prossimo aggiornamento carrello */
  }
};

/* ---- Mirror stato "drawer aperto" -> #site-cart-sidebar.sidebar--opened ----
   isDrawerOpen() del motore legge getElementById('site-cart-sidebar').classList.contains('sidebar--opened').
   Rispecchiamo l'attributo [open] del <theme-drawer id="cart-drawer">. */
function syncDrawerOpen() {
  const alias = document.getElementById('site-cart-sidebar');
  if (!alias) return;
  const drawer = document.getElementById('cart-drawer');
  const open = !!(drawer && (drawer.hasAttribute('open') || drawer.open === true));
  alias.classList.toggle('sidebar--opened', open);
}
/* GUARDIA "resta aperto": durante una modifica carrello (remove col cestino, qty,
   add) un re-render puo' far perdere lo stato [open] del drawer. Entro una finestra
   dalla modifica, se [open] sparisce ma ci sono ancora prodotti, riapriamo. */
var keepOpenUntil = 0;
function armKeepOpen() { keepOpenUntil = Date.now() + 2500; }
function cartHasItems() {
  return !!document.querySelector('#cart-drawer .cart-items__media, #cart-drawer .cart-items__table tbody tr');
}
// il click sul cestino/qty puo' chiudere prima ancora del cartLinesUpdate: arma in capture
document.addEventListener('click', function (e) {
  if (e.target.closest && e.target.closest('.cart-items__remove, .quantity-minus, .quantity-plus, [class*="cart-items__remove"]')) armKeepOpen();
}, true);

const drawerEl = document.getElementById('cart-drawer');
if (drawerEl) {
  var wasOpen = drawerEl.hasAttribute('open');
  new MutationObserver(function () {
    syncDrawerOpen();
    var nowOpen = drawerEl.hasAttribute('open');
    if (nowOpen && !wasOpen) setTimeout(pokeEngine, 60); // apertura -> fai girare il motore
    // chiusura non voluta durante una modifica carrello, col carrello non vuoto -> riapri
    if (!nowOpen && wasOpen && Date.now() < keepOpenUntil && cartHasItems()) {
      if (typeof drawerEl.open === 'function') { try { drawerEl.open(); } catch (e) {} }
    }
    wasOpen = drawerEl.hasAttribute('open');
  }).observe(drawerEl, { attributes: true, attributeFilter: ['open'] });
}
syncDrawerOpen();
// Mutazioni carrello NATIVE di Horizon (qty selector, remove, add): quando il
// morph è pronto (event.promise) risincronizza lo stato drawer e poka il motore.
document.addEventListener(StandardEvents.cartLinesUpdate, function (e) {
  armKeepOpen();
  var done = function () {
    setTimeout(function () {
      syncDrawerOpen();
      pokeEngine();
    }, 30);
  };
  if (e && e.promise && typeof e.promise.then === 'function') {
    e.promise.then(done).catch(done);
  } else {
    setTimeout(done, 350);
  }
});

/* Handler delegato per i bottoni "Aggiungi" nativi Horizon (snippets/faan-cart-add-form):
   upsell, gift, ecc. Aggiunge via FaanCart.addLine passando le proprietà riga. */
document.addEventListener('click', function (e) {
  var btn = e.target.closest && e.target.closest('[data-faan-add]');
  if (!btn || btn.disabled || btn.getAttribute('aria-busy') === 'true') return;
  var vid = btn.getAttribute('data-faan-variant');
  if (!vid || !window.FaanCart) return;
  var props = {};
  (btn.getAttribute('data-faan-props') || '').split('&').forEach(function (p) {
    if (!p) return;
    var kv = p.split('=');
    if (kv[0]) props[kv[0]] = kv[1] || 'true';
  });
  btn.setAttribute('aria-busy', 'true');
  btn.classList.add('is-loading');
  window.FaanCart.addLine(vid, props)
    .then(function () { return window.refreshCart(); })
    .catch(function () {})
    .then(function () {
      btn.removeAttribute('aria-busy');
      btn.classList.remove('is-loading');
    });
});

/* Dropdown quantita' (.faan-qty-select in cart-products.liquid): al change
   cambiamo la riga e ri-renderizziamo il drawer. Armiamo keepOpen cosi' un
   eventuale re-render non chiude il drawer. */
document.addEventListener('change', function (e) {
  var sel = e.target && e.target.closest && e.target.closest('[data-faan-qty]');
  if (!sel) return;
  var key = sel.getAttribute('data-line-key');
  var qty = sel.value;
  if (!key || !window.FaanCart || !window.FaanCart.changeLine) return;
  armKeepOpen();
  sel.disabled = true;
  window.FaanCart.changeLine(key, qty)
    .then(function () { return window.refreshCart(); })
    .catch(function () {})
    .then(function () { try { sel.disabled = false; } catch (e) {} });
});

/* Quick view -> handoff al drawer: aggiungendo da un <dialog> modale (quick-add
   modal) l'auto_open del carrello non scatta. Alla modifica carrello, se il
   modale quick-add e' aperto lo chiudiamo e apriamo il cart drawer. */
document.addEventListener(StandardEvents.cartLinesUpdate, function () {
  var modal = document.querySelector('.quick-add-modal[open], dialog.quick-add-modal[open]');
  if (!modal) return;
  /* 01/10 FIX pagina bloccata: chiudere il <dialog> nativo con modal.close() saltava
     la chiusura di Horizon (closeDialog -> unlockScroll), quindi html[scroll-lock]
     restava attivo anche dopo aver chiuso il carrello e la pagina non scorreva piu'
     (soprattutto iPhone). Chiudo tramite il componente Horizon; fallback al nativo. */
  var host = modal.closest('quick-add-dialog, dialog-component');
  try {
    if (host && typeof host.closeDialog === 'function') host.closeDialog();
    else modal.close();
  } catch (e) { try { modal.close(); } catch (e2) {} }
  setTimeout(function () {
    var d = document.getElementById('cart-drawer');
    if (d && typeof d.open === 'function' && !(d.hasAttribute('open') || d.open === true)) {
      try { d.open(); } catch (e) {}
    }
  }, 80);
});

/* Tasto "Continua lo shopping" (cart-summary.liquid): chiude il drawer. */
document.addEventListener('click', function (e) {
  var btn = e.target && e.target.closest && e.target.closest('[data-faan-continue-shopping]');
  if (!btn) return;
  e.preventDefault();
  var d = document.getElementById('cart-drawer');
  if (d && typeof d.close === 'function') { try { d.close(); } catch (err) {} }
});

/* FIX cestino chiude il drawer: Horizon chiude il drawer su "click backdrop"
   (theme-drawer #onBackdropClick -> isClickedOutside -> panel.contains(target)).
   Il click sul cestino/qty RIMUOVE la riga (target) durante il click -> target
   staccato -> risulta "fuori" -> chiude. Fermiamo la propagazione di questi click
   PRIMA che raggiungano il <dialog>, agganciandoci al cart-drawer-component
   (dentro il dialog, quindi bubbling PRIMA del panel). La rimozione riga avviene
   comunque (handler piu' interni, gia' scattati). */
function protectDrawerFromBackdropClose() {
  var comp = document.querySelector('#cart-drawer cart-drawer-component');
  if (!comp || comp.__faanBackdropGuard) return;
  comp.__faanBackdropGuard = true;
  comp.addEventListener('click', function (e) {
    var t = e.target;
    if (t && t.closest && t.closest('.cart-items__remove, .cart-items__quantity, cart-quantity-selector-component, .quantity-selector, .faan-qty-select, [data-faan-add], .faan-cart-sheet__row-button')) {
      e.stopPropagation();
    }
  }, false);
}
protectDrawerFromBackdropClose();
document.addEventListener(StandardEvents.cartLinesUpdate, function () { setTimeout(protectDrawerFromBackdropClose, 50); });

document.dispatchEvent(new CustomEvent('faan-cart:bridge-ready'));
